import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { m as motion } from 'framer-motion';
import { ShieldAlert } from 'lucide-react';
import { useSession } from '@/stores/session';
import { securitySettingsService } from '@/services/securitySettings';
import { mfaService } from '@/services/mfa';
import { PageSkeleton } from '@/components/system/LazyPage';
import { ErrorState } from '@/components/ui/ErrorState';

// 2-nov (bundle): la inscripción solo se carga si hay que mostrarla.
const MfaPage = lazy(() => import('@/features/account/MfaPage').then((m) => ({ default: m.MfaPage })));

/** MFA obligatorio para el equipo (VLT-307): si la firma lo exige, ningún rol
 *  no-cliente entra sin su segundo factor — la app entera queda detrás de la
 *  inscripción (una sola vez, 2 minutos). Los clientes no se bloquean: para
 *  ellos el MFA es opcional desde su cuenta. */
export function MfaGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const role = useSession((s) => s.role);
  const [state, setState] = useState<'checking' | 'blocked' | 'ok' | 'error'>('checking');
  const [tryN, setTry] = useState(0);

  useEffect(() => {
    let alive = true;
    setState('checking');
    (async () => {
      if (!role || role === 'client') {
        if (alive) setState('ok');
        return;
      }
      const cfg = await securitySettingsService.get();
      if (!cfg.mfaRequiredStaff) {
        if (alive) setState('ok');
        return;
      }
      try {
        const st = await mfaService.status();
        if (alive) setState(st.enrolled ? 'ok' : 'blocked');
      } catch {
        // Sin poder comprobarlo no se deja pasar NI se pide inscribirse de
        // nuevo: se dice la verdad y se ofrece reintentar.
        if (alive) setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [role, tryN]);

  if (state === 'checking') return null;
  if (state === 'error') {
    return (
      <div className="grid min-h-dvh place-items-center px-4">
        <div className="w-full max-w-md">
          <ErrorState what={t('mfa.statusWhat')} onRetry={() => setTry((n) => n + 1)} />
        </div>
      </div>
    );
  }
  if (state === 'blocked') {
    return (
      <div className="min-h-dvh px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          role="alert"
          className="mx-auto mb-5 flex w-full max-w-2xl items-start gap-3 rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3.5"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-none text-warning" />
          <div>
            <p className="font-display text-sm font-semibold">{t('mfa.requiredTitle')}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{t('mfa.requiredHint')}</p>
          </div>
        </motion.div>
        <Suspense fallback={<PageSkeleton />}><MfaPage required onEnrolled={() => setState('ok')} /></Suspense>
      </div>
    );
  }
  return children;
}
